'use client';

import { useQuery } from '@tanstack/react-query';
import Image from 'next/image';
import Link from 'next/link';
import { productDetailOptions } from '~/app/products/[pid]/_queries';
import { ShopId } from '~/constants/shop';
import { authStorage } from '~/services/auth';
import { getConditionStatus } from '~/utils/product-service';
import { getPaymentNotices } from '~/utils/shop-service';

type Props = {
  pid: string;
};

export const StaticProductDetailSeo = ({ pid }: Props) => {
  const { data } = useQuery(productDetailOptions({ pid, uid: authStorage.getUid() }));

  return (
    <main>
      <figure>
        {[...Array(data?.product.image.count).keys()].map((count) => (
          <Image
            key={count}
            src={data?.product.image.url?.replace('{cnt}', (count + 1).toString())?.replace('{res}', '360') ?? ''}
            alt={`${data?.product.name} 상품 이미지 ${count + 1}`}
            width={360}
            height={360}
          />
        ))}
      </figure>

      <article>
        <header>
          <h1>{data?.product.name}</h1>
          <h2>{`${data?.product.price.toLocaleString()}원`}</h2>

          <small aria-label="가격 포함 사항">해외 배송비, 관부가세가 모두 포함된 가격</small>
        </header>

        <section>
          <time dateTime={data?.product.updatedAt?.toISOString()} title="상품 업데이트 시간">
            {data?.product.updatedBefore}
          </time>
          <dl>
            <dt>찜</dt>
            <dd>{data?.product.metric.favoriteCount.toLocaleString()}</dd>
          </dl>
        </section>

        <section>
          <h3>결제 혜택</h3>
          <ul aria-label="결제 혜택 목록">
            {data?.payBenefit?.contents.map((content) => <li key={content}>{content}</li>)}
            {data?.payBenefit?.moreContents?.map((content) => <li key={content}>{content}</li>)}
          </ul>
        </section>
      </article>

      <nav aria-label="breadcrumb">
        <ol style={{ display: 'flex' }}>
          {data?.product.categories?.map((category, index, array) => (
            <li key={category.id}>
              <Link href={`/category/${category.id}`}>{category.name}</Link>
              {index < array.length - 1 && '>'}
            </li>
          ))}
        </ol>
      </nav>

      <dl>
        <dt>상품상태</dt>
        <dd>{getConditionStatus(data?.product.condition)}</dd>

        <dt>사이즈</dt>
        <dd>{data?.product.size}</dd>

        <dt>수량</dt>
        <dd>{data?.product.qty}</dd>
      </dl>

      <section aria-label="상품 설명">
        <h3>상품 설명</h3>
        {data?.product.description}
      </section>

      <section>
        <h4>결제 전 확인해 주세요.</h4>
        <ul>
          {getPaymentNotices(data?.shop.shopId as ShopId).map((notice) => (
            <li key={notice}>{notice}</li>
          ))}
        </ul>
      </section>
    </main>
  );
};
